import { Box, Flex, Heading, Stack, Text } from "@chakra-ui/react"

import { HtmlContent } from "@/components/Common/HtmlContent"
import { stripOptionPrefix } from "@/utils/text-format"
import "./print.css"

export interface PrintableQuizQuestion {
  id: string
  order: number
  questionText: string
  options: string[]
  correctAnswerIndex?: number | null
  rationale?: string | null
}

export interface PrintableQuizOptions {
  includeAnswers: boolean
  includeRationales: boolean
  twoColumns: boolean
}

export interface PrintableQuizProps {
  quiz: {
    id: string
    name: string
    description?: string | null
    questions: PrintableQuizQuestion[]
  }
  options: PrintableQuizOptions
}

export function PrintableQuiz({ quiz, options }: PrintableQuizProps) {
  const { includeAnswers, includeRationales, twoColumns } = options

  return (
    <Box
      data-testid="printable-quiz"
      className={twoColumns ? "printable-quiz-two-columns" : undefined}
    >
      <Stack gap={1} mb={6} data-testid="printable-quiz-header">
        <Heading size="lg">{quiz.name}</Heading>
        {quiz.description ? (
          <Text color="fg.muted">{quiz.description}</Text>
        ) : null}
      </Stack>

      <Stack gap={4} data-testid="printable-quiz-question-list">
        {quiz.questions.map((question) => (
          <Stack
            key={question.id}
            gap={2}
            data-testid={`printable-question-${question.id}`}
            className="printable-question"
          >
            <HtmlContent
              fontWeight="medium"
              content={`${question.order}. ${question.questionText}`}
            />
            <Stack
              gap={1}
              data-testid={`printable-question-options-${question.id}`}
            >
              {question.options.map((optionText, index) => (
                <Flex key={index} gap={2} align="flex-start">
                  <Text as="span" fontWeight="semibold">
                    {String.fromCharCode(65 + index)}.
                  </Text>
                  <HtmlContent
                    flex="1"
                    content={stripOptionPrefix(optionText)}
                  />
                </Flex>
              ))}
            </Stack>
            {includeAnswers &&
            typeof question.correctAnswerIndex === "number" ? (
              <Box
                data-testid={`printable-question-answer-${question.id}`}
                fontSize="sm"
                color="fg.muted"
              >
                Correct Answer:{" "}
                {String.fromCharCode(65 + question.correctAnswerIndex)}
              </Box>
            ) : null}
            {includeRationales && question.rationale ? (
              <Box
                data-testid={`printable-question-rationale-${question.id}`}
                fontSize="sm"
                color="fg.muted"
              >
                <Text as="span">Rationale: </Text>
                <HtmlContent as="span" content={question.rationale} />
              </Box>
            ) : null}
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
